import { useEffect, useState, KeyboardEvent } from "react";
import { Pencil, Plus, Trash2, X, GripVertical } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useMetadataQueue, type StaleSummary } from "@/hooks/useMetadataQueue";
import {
  useCreateProjectCustomField,
  useDeleteProjectCustomField,
  useProjectCustomFields,
  useUpdateProjectCustomField,
} from "@/hooks/useProjectCustomFields";
import type {
  CustomFieldType,
  ProjectCustomField,
} from "@/services/projectCustomFieldsApi";

const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Free text",
  textarea: "Long text",
  single_select: "Single-select",
  multi_select: "Multi-select",
  date: "Date",
  numeric: "Numeric",
};

const SELECT_TYPES = new Set<CustomFieldType>(["single_select", "multi_select"]);

interface ProjectCustomFieldsProps {
  projectId: string;
  disabled?: boolean;
}

const ProjectCustomFields = ({ projectId, disabled }: ProjectCustomFieldsProps) => {
  const { data: fields = [], isLoading } = useProjectCustomFields(projectId);
  const createField = useCreateProjectCustomField(projectId);
  const updateField = useUpdateProjectCustomField(projectId);
  const deleteField = useDeleteProjectCustomField(projectId);
  const { toast } = useToast();
  const queue = useMetadataQueue();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectCustomField | null>(null);
  const [staleSummary, setStaleSummary] = useState<StaleSummary | null>(null);
  const [isReExtracting, setIsReExtracting] = useState(false);

  const handleAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleEdit = (field: ProjectCustomField) => {
    setEditing(field);
    setDialogOpen(true);
  };

  const errorToast = (title: string) => (err: unknown) =>
    toast({ title, description: err instanceof Error ? err.message : "Unexpected error", variant: "destructive" });

  const handleDelete = (field: ProjectCustomField) => {
    if (!globalThis.confirm(`Delete custom field "${field.name}"?`)) return;
    deleteField.mutate(field.id, { onError: errorToast("Delete failed") });
  };

  // After a successful create/update, ask the server whether any docs now
  // have an outdated extraction. If yes, surface a modal with a one-click
  // re-extract CTA. The backend's re-extract endpoint diffs per-doc and
  // only sends the new/changed fields to the LLM.
  const promptReExtractIfStale = async () => {
    try {
      const summary = await queue.fetchStaleSummary(projectId);
      setStaleSummary(summary.stale_count === 0 ? null : summary);
    } catch {
      // Stale-summary is non-critical — fail silently rather than blocking save.
    }
  };

  const handleReExtract = async () => {
    if (!staleSummary) return;
    setIsReExtracting(true);
    try {
      const result = await queue.reExtract(
        projectId,
        staleSummary.stale_document_ids.map((id) => ({ document_id: id })),
      );
      const enqueued = result.enqueued.length;
      if (enqueued === 0) {
        toast({ title: "Nothing to update", description: "All documents are already up to date." });
      } else {
        toast({
          title: `Re-extracting ${enqueued} document${enqueued === 1 ? "" : "s"}`,
          description: "Check the Evidence Base page for progress.",
        });
      }
      setStaleSummary(null);
    } catch (err) {
      toast({
        title: "Re-extract failed",
        description: err instanceof Error ? err.message : "Unexpected error",
        variant: "destructive",
      });
    } finally {
      setIsReExtracting(false);
    }
  };

  const handleDialogSubmit = (values: DialogValues) => {
    const onDone = () => {
      setDialogOpen(false);
      void promptReExtractIfStale();
    };
    if (editing) {
      updateField.mutate(
        { fieldId: editing.id, data: values },
        { onSuccess: onDone, onError: errorToast("Save failed") },
      );
    } else {
      createField.mutate(values, { onSuccess: onDone, onError: errorToast("Create failed") });
    }
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-brand-navy">Custom Fields</h2>
          <p className="text-sm text-muted-foreground">
            Fields defined here are presented when team members upload sources to this project.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleAdd}
          disabled={disabled}
          className="gap-1"
        >
          <Plus className="w-4 h-4" /> Add Field
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Type</TableHead>
              <TableHead className="w-24">Required</TableHead>
              <TableHead>Options</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  Loading fields…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && fields.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  No custom fields yet. Click <strong>Add Field</strong> to define one.
                </TableCell>
              </TableRow>
            )}
            {fields.map((field) => (
              <TableRow key={field.id}>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50" />
                    <span className="font-medium">{field.name}</span>
                  </div>
                  {field.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 ml-5">{field.description}</p>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="font-normal">
                    {FIELD_TYPE_LABELS[field.field_type]}
                  </Badge>
                </TableCell>
                <TableCell>
                  {field.required ? (
                    <Badge variant="outline" className="font-normal">Required</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {SELECT_TYPES.has(field.field_type) && field.options ? (
                    <div className="flex flex-wrap gap-1">
                      {field.options.map((opt) => (
                        <Badge key={opt} variant="secondary" className="font-normal text-xs">
                          {opt}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(field)}
                      disabled={disabled}
                      title="Edit field"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(field)}
                      disabled={disabled}
                      title="Delete field"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CustomFieldDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        field={editing}
        isSubmitting={createField.isPending || updateField.isPending}
        onSubmit={handleDialogSubmit}
      />

      <AlertDialog
        open={staleSummary !== null}
        onOpenChange={(next) => {
          if (!next && !isReExtracting) setStaleSummary(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {staleSummary
                ? `${staleSummary.stale_count} document${staleSummary.stale_count === 1 ? "" : "s"} need${staleSummary.stale_count === 1 ? "s" : ""} updating for the new schema`
                : ""}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Only the fields that changed will be re-extracted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReExtracting}>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleReExtract();
              }}
              disabled={isReExtracting}
            >
              {isReExtracting ? "Re-extracting…" : "Re-extract now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default ProjectCustomFields;

// ─── Dialog ────────────────────────────────────────────────────────────────

interface DialogValues {
  name: string;
  field_type: CustomFieldType;
  options: string[] | null;
  required: boolean;
  description: string | null;
}

interface CustomFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: ProjectCustomField | null;
  isSubmitting: boolean;
  onSubmit: (values: DialogValues) => void;
}

const CustomFieldDialog = ({
  open,
  onOpenChange,
  field,
  isSubmitting,
  onSubmit,
}: CustomFieldDialogProps) => {
  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("text");
  const [options, setOptions] = useState<string[]>([]);
  const [optionInput, setOptionInput] = useState("");
  const [required, setRequired] = useState(false);
  const [description, setDescription] = useState("");

  // Hydrate when the dialog opens; clear when it closes.
  useEffect(() => {
    if (!open) return;
    if (field) {
      setName(field.name);
      setFieldType(field.field_type);
      setOptions(field.options ?? []);
      setRequired(field.required);
      setDescription(field.description ?? "");
    } else {
      setName("");
      setFieldType("text");
      setOptions([]);
      setRequired(false);
      setDescription("");
    }
    setOptionInput("");
  }, [open, field]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
  };

  const addOption = (raw: string) => {
    const v = raw.trim();
    if (!v || options.includes(v)) return;
    setOptions((prev) => [...prev, v]);
  };

  const handleOptionKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addOption(optionInput);
      setOptionInput("");
    } else if (e.key === "Backspace" && optionInput === "" && options.length > 0) {
      setOptions((prev) => prev.slice(0, -1));
    }
  };

  const isSelect = SELECT_TYPES.has(fieldType);
  const isValid =
    name.trim().length > 0 && (!isSelect || options.length > 0);

  const submit = () => {
    if (!isValid) return;
    onSubmit({
      name: name.trim(),
      field_type: fieldType,
      options: isSelect ? options : null,
      required,
      description: description.trim() ? description.trim() : null,
    });
  };

  let submitLabel = field ? "Save Changes" : "Add Field";
  if (isSubmitting) submitLabel = "Saving…";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{field ? "Edit Custom Field" : "Add Custom Field"}</DialogTitle>
          <DialogDescription>
            Defines a metadata property captured when uploading sources to this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="font-semibold">Field Name <span className="text-destructive">*</span></Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Year of Research"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="font-semibold">Field Type <span className="text-destructive">*</span></Label>
            <Select value={fieldType} onValueChange={(v) => setFieldType(v as CustomFieldType)}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Free text (single line)</SelectItem>
                <SelectItem value="textarea">Long text (multi-line)</SelectItem>
                <SelectItem value="single_select">Single-select (from a list)</SelectItem>
                <SelectItem value="multi_select">Multi-select (from a list)</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="numeric">Numeric</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isSelect && (
            <div>
              <Label className="font-semibold">Options <span className="text-destructive">*</span></Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 items-center min-h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm">
                {options.map((opt) => (
                  <Badge key={opt} variant="secondary" className="gap-1 pr-1 font-normal">
                    {opt}
                    <button
                      type="button"
                      onClick={() => setOptions((prev) => prev.filter((o) => o !== opt))}
                      className="rounded-full hover:bg-secondary-foreground/20"
                      aria-label={`Remove option ${opt}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                <input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={handleOptionKeyDown}
                  onBlur={() => {
                    if (optionInput.trim()) {
                      addOption(optionInput);
                      setOptionInput("");
                    }
                  }}
                  placeholder={options.length === 0 ? "Type an option and press Enter…" : ""}
                  className="flex-1 min-w-[140px] bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="custom-field-required"
              checked={required}
              onCheckedChange={(v) => setRequired(v === true)}
            />
            <Label htmlFor="custom-field-required" className="font-normal cursor-pointer">
              Required at upload time
            </Label>
          </div>

          <div>
            <Label className="font-semibold">Extraction Guidance</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={'Explain what this field should capture — shown to users at upload time and used to guide AI-suggested extraction. E.g., "the statistical method used, not the overall study design."'}
              className="mt-1.5 min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-brand-teal text-white hover:bg-brand-teal/90"
            onClick={submit}
            disabled={!isValid || isSubmitting}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
